import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Pagination from '../../../components/Pagination/Pagination';
import Button from '../../../components/Button/Button';
import Input from '../../../components/input/Input';
import DatePicker from '../../../components/DatePicker/DatePicker';
import { API_CONFIG } from '../../../config/api';
import { authService } from '../../../services/authService';
import './style.css';

interface ParcelaConciliacao {
    chaveConciliacao: string;
    dtVencimento: string;
    seuNumero: string;
    vlNominal: number;
}

interface ContratoConciliacao {
    numeroDocumento: string;
    idContratoRegistradora?: string;
    parcelas: ParcelaConciliacao[];
}

type TipoPendente = 'registrar' | 'liquidar';

export interface ConciliacaoPaginaResponse {
    cnpjFundo: string;
    nomeFundo: string;
    tipo: TipoPendente;
    page: number;
    size: number;
    total: number;
    totalPaginas: number;
    contratos: ContratoConciliacao[];
}

export interface ConciliacaoFundoDetalhes {
    cnpjFundo: string;
    nomeFundo: string;
    registrar: ConciliacaoPaginaResponse;
    liquidar: ConciliacaoPaginaResponse;
}

interface DetalhesFiltros {
    numeroDocumento?: string;
    seuNumero?: string;
}

export interface ConciliacaoItemInfo {
    carteira: string;
    cnpjFundo: string;
    responsavel: string;
    ultimaConciliacao: string;
    idUltimaConciliacao: string;
    qtdParcelasEstoque: number;
    qtdParcelasRegistradora: number;
}

const DEFAULT_PAGE_SIZE = 10;

const TIPOS: { key: TipoPendente; label: string }[] = [
    { key: 'registrar', label: 'Pendentes a Registrar' },
    { key: 'liquidar', label: 'Pendentes a Liquidar' },
];

const ACTION_ENDPOINTS: Record<TipoPendente, string> = {
    registrar: '/api/v1/conciliacoes/registrar',
    liquidar: '/api/v1/conciliacoes/liquidar',
};

interface PaginaParams {
    tipo: TipoPendente;
    page: number;
    size: number;
    numeroDocumento?: string;
    seuNumero?: string;
}

export async function fetchConciliacaoPagina(
    cnpjFundo: string,
    params: PaginaParams
): Promise<ConciliacaoPaginaResponse> {
    const token = await authService.getValidToken();
    const query = new URLSearchParams();
    query.set('tipo', params.tipo);
    query.set('page', String(params.page));
    query.set('size', String(params.size));

    if (params.numeroDocumento?.trim()) {
        query.set('numeroDocumento', params.numeroDocumento.trim());
    }
    if (params.seuNumero?.trim()) {
        query.set('seuNumero', params.seuNumero.trim());
    }

    const response = await fetch(
        `${API_CONFIG.BASE_URL_JAVA}/api/v1/conciliacoes/divergencias/completo/${cnpjFundo.trim()}?${query.toString()}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        }
    );

    if (!response.ok) {
        let msg = `Erro ao buscar detalhes: ${response.statusText}`;
        try {
            const body = await response.json();
            if (body?.message) msg = body.message;
        } catch { /* no-op */ }
        throw new Error(msg);
    }

    return response.json();
}

export async function fetchFundoDetalhes(
    cnpjFundo: string,
    opts?: { size?: number; filters?: DetalhesFiltros }
): Promise<ConciliacaoFundoDetalhes> {
    const size = opts?.size ?? DEFAULT_PAGE_SIZE;
    const filters = opts?.filters ?? {};

    const [registrar, liquidar] = await Promise.all([
        fetchConciliacaoPagina(cnpjFundo, { tipo: 'registrar', page: 0, size, ...filters }),
        fetchConciliacaoPagina(cnpjFundo, { tipo: 'liquidar', page: 0, size, ...filters }),
    ]);

    return {
        cnpjFundo: cnpjFundo.trim(),
        nomeFundo: registrar.nomeFundo || liquidar.nomeFundo || '',
        registrar,
        liquidar,
    };
}

export async function fetchConciliacaoDetalhes(
    cnpjsFundo: string[],
    opts?: { size?: number; filters?: DetalhesFiltros }
): Promise<ConciliacaoFundoDetalhes[]> {
    const cnpjs = cnpjsFundo.map((c) => c.trim()).filter(Boolean);
    return Promise.all(cnpjs.map((cnpj) => fetchFundoDetalhes(cnpj, opts)));
}

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDateLabel = (value?: string): string => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    }).format(d);
};

const todayIso = (): string => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const somaParcelas = (contrato: ContratoConciliacao): number =>
    (contrato.parcelas || []).reduce((acc, p) => acc + (p.vlNominal || 0), 0);

const ConciliacaoDetalhe: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const navState = location.state as {
        items?: ConciliacaoItemInfo[];
        item?: ConciliacaoItemInfo;
        initialData?: ConciliacaoFundoDetalhes[];
    } | null;

    const items = useMemo<ConciliacaoItemInfo[]>(() => {
        if (navState?.items?.length) return navState.items;
        if (navState?.item) return [navState.item];
        return [];
    }, [navState]);

    const [activeIndex, setActiveIndex] = useState(0);
    const item = items[activeIndex] ?? null;

    const [allData, setAllData] = useState<ConciliacaoFundoDetalhes[]>(navState?.initialData ?? []);
    const [fundoAtual, setFundoAtual] = useState<ConciliacaoFundoDetalhes | null>(
        navState?.initialData?.[0] ?? null
    );
    const [pagina, setPagina] = useState<ConciliacaoPaginaResponse | null>(
        navState?.initialData?.[0]?.registrar ?? null
    );

    const [activeTab, setActiveTab] = useState<TipoPendente>('registrar');
    const [numeroDocumentoFiltro, setNumeroDocumentoFiltro] = useState('');
    const [seuNumeroFiltro, setSeuNumeroFiltro] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [appliedFilters, setAppliedFilters] = useState<DetalhesFiltros>({ numeroDocumento: '', seuNumero: '' });

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [refDate, setRefDate] = useState<string>(todayIso());
    const [submitting, setSubmitting] = useState(false);
    const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const handleVoltar = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const cnpjsFundo = useMemo(() => items.map((it) => it.cnpjFundo), [items]);

    const reloadTodos = useCallback(
        async (filters: DetalhesFiltros, size: number, index: number, tab: TipoPendente) => {
            if (!cnpjsFundo.length) return;

            setLoading(true);
            setError(null);

            try {
                const result = await fetchConciliacaoDetalhes(cnpjsFundo, { size, filters });
                setAllData(result);
                const fundo = result[index] ?? result[0] ?? null;
                setFundoAtual(fundo);
                setPagina(fundo ? fundo[tab] : null);
                setCurrentPage(0);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Erro ao carregar detalhes');
                setAllData([]);
                setFundoAtual(null);
                setPagina(null);
            } finally {
                setLoading(false);
            }
        },
        [cnpjsFundo]
    );

    const carregarPagina = useCallback(
        async (page: number, size: number, tab: TipoPendente, filters: DetalhesFiltros) => {
            const cnpj = items[activeIndex]?.cnpjFundo;
            if (!cnpj) return;

            setPageLoading(true);
            setError(null);

            try {
                const result = await fetchConciliacaoPagina(cnpj, { tipo: tab, page, size, ...filters });
                setPagina(result);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Erro ao carregar página');
            } finally {
                setPageLoading(false);
            }
        },
        [items, activeIndex]
    );

    useEffect(() => {
        if (!items.length) {
            navigate('/conciliacoes/gerais', { replace: true });
            return;
        }
        if (!navState?.initialData) {
            reloadTodos({ numeroDocumento: '', seuNumero: '' }, DEFAULT_PAGE_SIZE, 0, 'registrar');
        }
    }, []);

    const handleSelectFund = (index: number) => {
        if (index === activeIndex || submitting || loading) return;
        if (!items[index]) return;

        setActiveIndex(index);
        setActiveTab('registrar');
        setSelected(new Set());
        setExpanded(new Set());
        setActionMsg(null);
        setCurrentPage(0);

        const fundo = allData[index] ?? null;
        setFundoAtual(fundo);
        setPagina(fundo ? fundo.registrar : null);

        if (!fundo) {
            reloadTodos(appliedFilters, pageSize, index, 'registrar');
        }
    };

    const handleSearch = () => {
        if (!cnpjsFundo.length) return;
        const novosFiltros = { numeroDocumento: numeroDocumentoFiltro, seuNumero: seuNumeroFiltro };
        setAppliedFilters(novosFiltros);
        setSelected(new Set());
        setExpanded(new Set());
        reloadTodos(novosFiltros, pageSize, activeIndex, activeTab);
    };

    const handleClearFilters = () => {
        if (!cnpjsFundo.length) return;
        const novosFiltros = { numeroDocumento: '', seuNumero: '' };
        setNumeroDocumentoFiltro('');
        setSeuNumeroFiltro('');
        setAppliedFilters(novosFiltros);
        setSelected(new Set());
        setExpanded(new Set());
        reloadTodos(novosFiltros, pageSize, activeIndex, activeTab);
    };

    const handleTabChange = (key: TipoPendente) => {
        setActiveTab(key);
        setCurrentPage(0);
        setSelected(new Set());
        setExpanded(new Set());
        setActionMsg(null);
        setPagina(fundoAtual ? fundoAtual[key] : null);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        carregarPagina(page, pageSize, activeTab, appliedFilters);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0);
        reloadTodos(appliedFilters, size, activeIndex, activeTab);
    };

    const getContratoKey = useCallback(
        (contrato: ContratoConciliacao): string =>
            activeTab === 'registrar'
                ? contrato.numeroDocumento
                : (contrato.idContratoRegistradora ?? contrato.numeroDocumento),
        [activeTab]
    );

    const pageContratos: ContratoConciliacao[] = pagina?.contratos ?? [];
    const totalElements = pagina?.total ?? 0;
    const totalPages = Math.max(1, pagina?.totalPaginas ?? 1);

    const allPageSelected =
        pageContratos.length > 0 && pageContratos.every((c) => selected.has(getContratoKey(c)));

    const toggleSelect = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSelectAllPage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            const keys = pageContratos.map(getContratoKey);
            const allIn = keys.every((k) => next.has(k));
            if (allIn) keys.forEach((k) => next.delete(k));
            else keys.forEach((k) => next.add(k));
            return next;
        });
    };

    const toggleExpand = (key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleAction = async () => {
        const keys = Array.from(selected);
        if (!keys.length || submitting || !item) return;

        const [year, month, day] = refDate.split('-');
        setSubmitting(true);
        setActionMsg(null);

        try {
            const token = await authService.getValidToken();
            const body =
                activeTab === 'registrar'
                    ? {
                        cnpjFundo: item.cnpjFundo.trim(),
                        year,
                        month,
                        day,
                        numeroDocumentos: keys,
                    }
                    : {
                        cnpjFundo: item.cnpjFundo.trim(),
                        carteira: item.carteira,
                        year,
                        month,
                        day,
                        idContratosRegistradora: keys,
                    };

            const response = await fetch(`${API_CONFIG.BASE_URL_JAVA}${ACTION_ENDPOINTS[activeTab]}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                let msg = `Erro ao ${activeTab}: ${response.statusText}`;
                try {
                    const errBody = await response.json();
                    if (errBody?.message) msg = errBody.message;
                } catch { /* no-op */ }
                throw new Error(msg);
            }

            const verbo = activeTab === 'registrar' ? 'registrado(s)' : 'liquidado(s)';
            setActionMsg({ type: 'success', text: `${keys.length} documento(s) ${verbo} com sucesso.` });
            setSelected(new Set());
            await reloadTodos(appliedFilters, pageSize, activeIndex, activeTab);
        } catch (err) {
            setActionMsg({
                type: 'error',
                text: err instanceof Error ? err.message : `Erro ao ${activeTab}`,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const actionLabel = activeTab === 'registrar' ? 'Registrar selecionados' : 'Liquidar selecionados';

    return (
        <div className="conciliacao-detalhe-container">
            <div className="conciliacao-detalhe-header">
                <button
                    type="button"
                    className="conciliacao-detalhe-back"
                    onClick={handleVoltar}
                    disabled={submitting}
                    title="Voltar para a listagem"
                >
                    ← Voltar
                </button>
                <h1 className="conciliacao-detalhe-title">Detalhes de conciliações</h1>
            </div>

            {items.length > 1 && (
                <div className="conciliacao-detalhe-fundos" role="tablist">
                    {items.map((it, i) => (
                        <button
                            key={`${it.carteira}__${it.cnpjFundo}`}
                            type="button"
                            role="tab"
                            aria-selected={i === activeIndex}
                            className={`conciliacao-fundo-tab${i === activeIndex ? ' conciliacao-fundo-tab--active' : ''}`}
                            onClick={() => handleSelectFund(i)}
                            disabled={submitting || loading}
                            title={it.cnpjFundo}
                        >
                            {it.carteira || it.cnpjFundo}
                        </button>
                    ))}
                </div>
            )}

            <div className="conciliacao-modal-info">
                <div className="conciliacao-modal-info-grid">
                    <div className="conciliacao-info-field">
                        <span className="conciliacao-info-label">Carteira</span>
                        <span className="conciliacao-info-value">{item?.carteira || '-'}</span>
                    </div>

                    <div className="conciliacao-info-field">
                        <span className="conciliacao-info-label">Cnpj da carteira</span>
                        <span className="conciliacao-info-value">{item?.cnpjFundo || '-'}</span>
                    </div>

                    <div className="conciliacao-info-field">
                        <span className="conciliacao-info-label">Aprovador</span>
                        <span className="conciliacao-info-value">{item?.responsavel || '-'}</span>
                    </div>
                    <div className="conciliacao-info-field">
                        <span className="conciliacao-info-label">Ultima conciliação</span>
                        <span className="conciliacao-info-value">
                            {formatDateLabel(item?.ultimaConciliacao)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="conciliacao-modal-summary">
                {TIPOS.map((tipo, i) => {
                    const total = (tipo.key === 'registrar'
                        ? fundoAtual?.registrar.total
                        : fundoAtual?.liquidar.total) ?? 0;
                    const colors: Record<TipoPendente, string> = { registrar: 'red', liquidar: 'green' };
                    return (
                        <React.Fragment key={tipo.key}>
                            {i > 0 && <div className="conciliacao-summary-divider" />}
                            <button
                                type="button"
                                className={`conciliacao-summary-badge conciliacao-summary-badge--${colors[tipo.key]}${activeTab === tipo.key ? ' conciliacao-summary-badge--active' : ''}`}
                                onClick={() => handleTabChange(tipo.key)}
                                aria-pressed={activeTab === tipo.key}
                                title={`Exibir ${tipo.label.toLowerCase()}`}
                            >
                                {tipo.label} ({total.toLocaleString('pt-BR')})
                            </button>
                        </React.Fragment>
                    );
                })}
            </div>

            <div className="conciliacao-modal-body">
                {!loading && (
                    <div className="conciliacao-modal-filters">
                        <Input
                            label="Número documento"
                            value={numeroDocumentoFiltro}
                            onChange={setNumeroDocumentoFiltro}
                            placeholder="Filtrar por número documento"
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        />

                        <Input
                            label="Seu número"
                            value={seuNumeroFiltro}
                            onChange={setSeuNumeroFiltro}
                            placeholder="Filtrar por seu número"
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        />

                        <div className="conciliacao-filter-actions">
                            <Button variant="secondary" onClick={handleClearFilters}>
                                Limpar
                            </Button>
                            <Button variant="primary" onClick={handleSearch}>
                                Buscar
                            </Button>
                        </div>
                    </div>
                )}

                {!loading && (
                    <div className="conciliacao-action-bar">
                        <div className="conciliacao-action-date">
                            <DatePicker
                                label="Data de referência"
                                value={refDate}
                                onChange={setRefDate}
                            />
                        </div>
                        <div className="conciliacao-action-info">
                            <span className="conciliacao-action-count">
                                {selected.size} selecionado(s)
                            </span>
                            <Button
                                variant="primary"
                                onClick={handleAction}
                                disabled={selected.size === 0 || submitting}
                            >
                                {submitting ? 'Enviando...' : actionLabel}
                            </Button>
                        </div>
                    </div>
                )}

                {actionMsg && (
                    <div className={`conciliacao-action-msg conciliacao-action-msg--${actionMsg.type}`}>
                        {actionMsg.text}
                    </div>
                )}

                {loading && (
                    <div className="conciliacao-modal-loading">
                        <div className="conciliacao-modal-spinner" />
                        <span>Iniciando conciliação...</span>
                    </div>
                )}

                {!loading && error && (
                    <div className="conciliacao-modal-empty">
                        <span>Erro: {error}</span>
                    </div>
                )}

                {!loading && !error && totalElements === 0 && (
                    <div className="conciliacao-modal-empty">
                        <span>Nenhum registro encontrado para esta categoria.</span>
                    </div>
                )}

                {!loading && !error && totalElements > 0 && (
                    <div
                        className="conciliacao-modal-table-wrapper"
                        aria-busy={pageLoading}
                        style={{ opacity: pageLoading ? 0.6 : 1, transition: 'opacity 0.15s' }}
                    >
                        <table className="conc-contratos-table">
                            <thead>
                                <tr>
                                    <th className="conc-col-check">
                                        <input
                                            type="checkbox"
                                            checked={allPageSelected}
                                            onChange={toggleSelectAllPage}
                                            title="Selecionar todos desta página"
                                        />
                                    </th>
                                    <th className="conc-col-expand" />
                                    <th>Nro. Documento</th>
                                    <th>Qtd. Parcelas</th>
                                    <th>Valor Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageContratos.map((contrato) => {
                                    const key = getContratoKey(contrato);
                                    const isExpanded = expanded.has(key);
                                    return (
                                        <React.Fragment key={key}>
                                            <tr className="conc-contrato-row">
                                                <td className="conc-col-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(key)}
                                                        onChange={() => toggleSelect(key)}
                                                    />
                                                </td>
                                                <td className="conc-col-expand">
                                                    <button
                                                        type="button"
                                                        className="conc-expand-btn"
                                                        onClick={() => toggleExpand(key)}
                                                        title={isExpanded ? 'Recolher parcelas' : 'Expandir parcelas'}
                                                        aria-expanded={isExpanded}
                                                    >
                                                        {isExpanded ? '▾' : '▸'}
                                                    </button>
                                                </td>
                                                <td>{contrato.numeroDocumento}</td>
                                                <td>{(contrato.parcelas || []).length}</td>
                                                <td>{formatCurrency(somaParcelas(contrato))}</td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="conc-parcelas-row">
                                                    <td colSpan={5}>
                                                        <table className="conc-parcelas-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Chave Conciliação</th>
                                                                    <th>Dt. Vencimento</th>
                                                                    <th>Seu Número</th>
                                                                    <th>Valor Nominal</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(contrato.parcelas || []).map((p) => (
                                                                    <tr key={p.chaveConciliacao}>
                                                                        <td>{p.chaveConciliacao}</td>
                                                                        <td>{p.dtVencimento}</td>
                                                                        <td>{p.seuNumero}</td>
                                                                        <td>{formatCurrency(p.vlNominal)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {!loading && totalElements > 0 && (
                <div className="conciliacao-modal-footer">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        totalElements={totalElements}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </div>
            )}
        </div>
    );
};

export default ConciliacaoDetalhe;
